/**
 * L4Takeover — feiringen `merke`: en hel skjerm med merket, tittel og
 * beskrivelse (Plan 02-15 / UI-SPEC §10; #155 — redesign steg 5b, skissen
 * `Stell_Feiring` variant `merke`).
 *
 * Skissen: eyebrow «Nytt merke!», merkeskiven i en stiplet ring, tittelen,
 * beskrivelsen, «Lukkes av seg selv om litt», og nederst «Se alle merkene
 * mine» og «Lukk». «Se alle merkene mine» vises bare når kallstedet gir
 * `onSeeAll` (CelebrationProvider sender til /utmerkelser).
 *
 * Storyboard:
 *   t=0      → skjermen åpnes (TakeoverScrim variant `screen`)
 *   t=200ms  → AchievementBadge zooms 0.6 → 1.0 via motion spring
 *              {stiffness:240, damping:22, mass:0.9}
 *   t=400ms  → title fades in below (250ms)
 *   t=400ms  → ConfettiBurst (160 phone / 320 tablet, 1200ms)
 *   t=600ms  → «Lukkes av seg selv om litt» fades in
 *   t=5000ms → auto-dismiss (or Lukk / tap-anywhere / Escape)
 *
 * Reduced-motion fallback (UI-SPEC §10):
 *   - skjermen vises med en gang
 *   - badge at 1.0 (no zoom)
 *   - no confetti
 *   - no sound (already gated by audio.ts via reducedMotion)
 *   - all tekst vises med en gang
 *   - auto-dismiss preserved at 5000ms
 *
 * Confetti scaling for tablet is handled inside ConfettiBurst via TIMINGS.
 *
 * Sound: the useCelebration hook fires `playSfx('achievement-takeover')`
 * with the same `reducedMotion` flag the component receives, so by the
 * time L4Takeover mounts the audio is already underway. We do NOT call
 * playSfx again here.
 */
import { useEffect, useId, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { TakeoverScrim } from '../../components/primitives/TakeoverScrim';
import { ConfettiBurst } from '../../components/primitives/ConfettiBurst';
import { AchievementBadge } from '../../components/primitives/AchievementBadge';
import { Button } from '../../components/primitives/Button';
import { TIMINGS, type ViewportClass } from './timings';

interface L4TakeoverProps {
  achievementKey: string;
  viewport: ViewportClass;
  reducedMotion: boolean;
  onComplete: () => void;
  /** «Se alle merkene mine». Uten den vises bare «Lukk». */
  onSeeAll?: () => void;
}

export function L4Takeover({
  achievementKey,
  viewport,
  reducedMotion,
  onComplete,
  onSeeAll,
}: L4TakeoverProps): ReactNode {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const titleId = useId();

  // Latest-ref, so the exit effect below never lists the provider's arrow as a
  // dependency (precedent: useCelebration.ts, L3ApprovedBurst.tsx).
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  // Auto-dismiss after 5000ms.
  useEffect(() => {
    const timer = setTimeout(() => {
      setOpen(false);
    }, TIMINGS.l4.autoDismiss);
    return () => {
      clearTimeout(timer);
    };
  }, []);

  // Handle dismissal — propagate to parent provider so the queue can advance.
  const handleDismiss = () => {
    setOpen(false);
  };

  // After the scrim closes, advance the queue. We use a small delay equal
  // to the scrimFadeOut so the visual exit completes before the next
  // celebration mounts.
  //
  // `onComplete` is deliberately NOT a dependency (issue #82).
  // CelebrationProvider passes a fresh `() => removeEvent(activeL4.id)` arrow
  // on every render, so listing it meant any provider re-render landing inside
  // this 240ms window cleared the timer and armed a new full-length one — the
  // queue advanced late, and a re-render every 240ms would have stalled it
  // indefinitely. The latest-ref keeps the arrow current without the churn.
  //
  // This effect owns a timer and its clearTimeout, so it MUST NEVER be
  // ref-guarded — React runs a cleanup BEFORE the re-run it would then skip,
  // which clears the timer and arms no replacement, and the takeover blocks
  // the whole L4 queue forever. Same asymmetry L3ApprovedBurst.tsx's header
  // explains at length.
  useEffect(() => {
    if (open) return undefined;
    const exitDelay = reducedMotion ? 0 : TIMINGS.l4.scrimFadeOut;
    const timer = setTimeout(() => {
      onCompleteRef.current();
    }, exitDelay);
    return () => {
      clearTimeout(timer);
    };
  }, [open, reducedMotion]);

  const title = t(`achievement.${achievementKey}.title`, { defaultValue: '' });
  const description = t(`achievement.${achievementKey}.description`, { defaultValue: '' });

  const badgeAnim = reducedMotion
    ? { initial: { scale: 1 }, animate: { scale: 1 } }
    : {
        initial: { scale: TIMINGS.l4.badgeZoom.from },
        animate: { scale: TIMINGS.l4.badgeZoom.to },
        transition: {
          type: 'spring' as const,
          stiffness: TIMINGS.l4.badgeZoom.spring.stiffness,
          damping: TIMINGS.l4.badgeZoom.spring.damping,
          mass: TIMINGS.l4.badgeZoom.spring.mass,
          delay: TIMINGS.l4.scrimFadeIn / 1000,
        },
      };

  const fadeIn = (delayMs: number, durationMs: number) =>
    reducedMotion
      ? { initial: { opacity: 1 }, animate: { opacity: 1 } }
      : {
          initial: { opacity: 0 },
          animate: { opacity: 1 },
          transition: { duration: durationMs / 1000, delay: delayMs / 1000 },
        };

  // Knappene lukker selv og stopper klikket, så det ikke også treffer
  // skjermens trykk-hvor-som-helst.
  const close = (e: MouseEvent) => {
    e.stopPropagation();
    setOpen(false);
  };

  return (
    <TakeoverScrim open={open} onDismiss={handleDismiss} variant="screen" labelledBy={titleId}>
      <div
        data-testid="l4-takeover"
        data-key={achievementKey}
        data-reduced-motion={reducedMotion ? 'true' : 'false'}
        className="relative flex min-h-dvh flex-col items-center px-6 pt-24 pb-[max(2.25rem,env(safe-area-inset-bottom))] text-center"
      >
        <div aria-hidden className="pointer-events-none absolute inset-0 bg-burst" />
        <div className="relative flex w-full max-w-sm flex-col items-center">
          <p className="m-0 mb-5.5 text-control font-extrabold tracking-widest text-accent-text uppercase">
            {t('celebrations.l4.eyebrow')}
          </p>
          <div className="relative flex h-60 w-60 items-center justify-center">
            <span
              aria-hidden
              className="absolute inset-0 rounded-full border-2 border-dashed border-secondary-line"
            />
            <motion.div {...badgeAnim}>
              <AchievementBadge keyName={achievementKey} unlocked secret={false} size={192} />
            </motion.div>
          </div>
          <motion.h1
            id={titleId}
            {...fadeIn(TIMINGS.l4.titleDelay, TIMINGS.l4.titleFadeIn)}
            className="m-0 mt-7 mb-1.5 text-3xl font-extrabold tracking-tight text-ink"
          >
            {title || t('celebrations.l4.eyebrow')}
          </motion.h1>
          {description ? (
            <motion.p
              {...fadeIn(TIMINGS.l4.titleDelay, TIMINGS.l4.titleFadeIn)}
              className="m-0 text-base text-ink-2"
            >
              {description}
            </motion.p>
          ) : null}
          <motion.p {...fadeIn(600, 250)} className="m-0 mt-4 text-control text-ink-2">
            {t('celebrations.l4.autoClose')}
          </motion.p>
        </div>
        {!reducedMotion ? <ConfettiBurst tier="l4" viewport={viewport} /> : null}
        <div className="relative mt-auto flex w-full max-w-sm flex-col gap-2 pt-10">
          {onSeeAll ? (
            <Button
              variant="solid"
              size={56}
              data-testid="l4-see-all"
              onClick={(e) => {
                onSeeAll();
                close(e);
              }}
            >
              {t('celebrations.l4.seeAll')}
            </Button>
          ) : null}
          <Button variant="ghost" size={52} data-testid="l4-close" onClick={close}>
            {t('celebrations.l4.close')}
          </Button>
        </div>
      </div>
    </TakeoverScrim>
  );
}
